import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
}) => {
  const isInternalUpdate = useRef(false);
  const [mode, setMode] = useState<'visual' | 'html'>('visual');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [3] },
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: 'text-indigo-400 underline' },
      }),
    ],
    content: value || '',
    onUpdate: ({ editor }) => {
      isInternalUpdate.current = true;
      const html = editor.getHTML().replace(/<p><\/p>/g, '<p><br></p>');
      onChange(html);
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm prose-invert max-w-none px-3 py-2 min-h-[80px] focus:outline-none text-slate-200 prose-headings:text-slate-100 prose-strong:text-slate-100 prose-a:text-indigo-400',
      },
    },
  });

  // Sync external value changes without disrupting typing
  useEffect(() => {
    if (!editor) return;
    if (isInternalUpdate.current) {
      isInternalUpdate.current = false;
      return;
    }
    const current = editor.getHTML();
    if (value !== current) {
      editor.commands.setContent(value || '', false);
    }
  }, [value, editor]);

  const handleModeSwitch = (newMode: 'visual' | 'html') => {
    if (newMode === 'visual' && editor) {
      editor.commands.setContent(value || '', false);
    }
    setMode(newMode);
  };

  if (!editor) return null;

  return (
    <div className='border border-slate-600 rounded overflow-hidden bg-slate-900'>
      {/* Toolbar */}
      <div className='flex items-center gap-0.5 px-2 py-1.5 border-b border-slate-700 bg-slate-800/80'>
        {mode === 'visual' && (
          <>
            <ToolbarBtn
              active={editor.isActive('bold')}
              onClick={() => editor.chain().focus().toggleBold().run()}
              title='Bold'
            >
              <span className='font-bold text-xs'>B</span>
            </ToolbarBtn>
            <ToolbarBtn
              active={editor.isActive('italic')}
              onClick={() => editor.chain().focus().toggleItalic().run()}
              title='Italic'
            >
              <span className='italic text-xs'>I</span>
            </ToolbarBtn>
            <ToolbarBtn
              active={editor.isActive('heading', { level: 3 })}
              onClick={() =>
                editor.chain().focus().toggleHeading({ level: 3 }).run()
              }
              title='Heading'
            >
              <span className='text-xs font-semibold'>H3</span>
            </ToolbarBtn>
            <div className='w-px h-4 bg-slate-700 mx-1' />
            <ToolbarBtn
              active={editor.isActive('bulletList')}
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              title='Bullet List'
            >
              <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M4 6h16M4 12h16M4 18h16' />
              </svg>
            </ToolbarBtn>
            <ToolbarBtn
              active={editor.isActive('orderedList')}
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              title='Numbered List'
            >
              <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M8 6h13M8 12h13M8 18h13' />
              </svg>
            </ToolbarBtn>
            <div className='w-px h-4 bg-slate-700 mx-1' />
            <ToolbarBtn
              active={editor.isActive('link')}
              onClick={() => {
                if (editor.isActive('link')) {
                  editor.chain().focus().unsetLink().run();
                  return;
                }
                const url = window.prompt('URL:');
                if (url) {
                  editor.chain().focus().setLink({ href: url }).run();
                }
              }}
              title='Link'
            >
              <svg className='w-3.5 h-3.5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101' />
                <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M10.172 13.828a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.102 1.101' />
              </svg>
            </ToolbarBtn>
            <div className='w-px h-4 bg-slate-700 mx-1' />
          </>
        )}
        <div className='flex ml-auto'>
          <button
            type='button'
            onClick={() => handleModeSwitch('visual')}
            className={`px-2 py-0.5 text-xs rounded-l transition-colors ${
              mode === 'visual'
                ? 'bg-indigo-600/40 text-indigo-300'
                : 'text-slate-400 hover:text-slate-200 bg-slate-700/50'
            }`}
          >
            Visual
          </button>
          <button
            type='button'
            onClick={() => handleModeSwitch('html')}
            className={`px-2 py-0.5 text-xs rounded-r transition-colors ${
              mode === 'html'
                ? 'bg-indigo-600/40 text-indigo-300'
                : 'text-slate-400 hover:text-slate-200 bg-slate-700/50'
            }`}
          >
            HTML
          </button>
        </div>
      </div>
      {mode === 'visual' ? (
        <EditorContent editor={editor} />
      ) : (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className='w-full px-3 py-2 min-h-[80px] bg-slate-900 text-slate-200 text-sm font-mono focus:outline-none resize-y'
          spellCheck={false}
        />
      )}
    </div>
  );
};

const ToolbarBtn: React.FC<{
  active: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}> = ({ active, onClick, title, children }) => (
  <button
    type='button'
    onMouseDown={(e) => e.preventDefault()}
    onClick={onClick}
    title={title}
    className={`p-1.5 rounded transition-colors ${
      active
        ? 'bg-indigo-600/40 text-indigo-300'
        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-700/50'
    }`}
  >
    {children}
  </button>
);

export default RichTextEditor;
